import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Translation {
  id: string;
  source_language: string;
  target_language: string;
  source_text: string;
  translated_text: string;
  translation_type: 'text' | 'image';
  created_at: string;
}

const TranslationHistory = () => {
  const { user } = useAuth();
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from('translations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!error && data) {
        setTranslations(data as Translation[]);
      }
      setLoading(false);
    };

    fetchHistory();

    // Real-time subscription
    const channel = supabase
      .channel('translations-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'translations',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          setTranslations((prev) => [payload.new as Translation, ...prev]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (translations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Translation History</CardTitle>
          <CardDescription>No translations yet</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Your translation history will appear here once you start translating.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Translation History</CardTitle>
        <CardDescription>Your recent translations</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {translations.map((translation) => (
              <div
                key={translation.id}
                className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {translation.source_language} → {translation.target_language}
                    </Badge>
                    <Badge variant="secondary">{translation.translation_type}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(translation.created_at), { addSuffix: true })}
                  </span>
                </div>
                <p className="text-sm mb-2 line-clamp-2">{translation.source_text}</p>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {translation.translated_text}
                </p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default TranslationHistory;
